import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import api from '@/services/api';
import { Meta } from '@/store/Announcement/types';
import { QuickAdjustPlanogram } from './types';

const PER_PAGE = 20;

export const KEY_PLANOGRAMS_BY_STORE = 'KEY_PLANOGRAMS_BY_STORE';

interface QuickAdjustPlanogramData {
    data: QuickAdjustPlanogram[];
    meta: Meta;
}

interface PlanogramFilters {
    name?: string;
    barcode?: string;
    categoryId?: number;
    status?: string;
    orderBy?: string;
    orderDir?: string;
}

type GetPlanogramsByStoreParams = PlanogramFilters & {
    page: number;
    perPage: number;
};

const NON_RETRYABLE_STATUSES = [401, 404];

const getErrorStatus = (error: unknown) =>
    (error as { response?: { status?: number } } | null)?.response?.status;

export const getPlanogramsByStore = async (
    storeId: number,
    params: GetPlanogramsByStoreParams,
) => {
    const response = await api.get<QuickAdjustPlanogramData>(
        `/store/auth/${storeId}/planogram`,
        { params, skipAuthLogout: true } as AxiosRequestConfig,
    );
    return response.data;
};

export function usePlanogramsByStore(
    storeId: number | undefined,
    filters: PlanogramFilters = {},
) {
    const query = useInfiniteQuery({
        queryKey: [KEY_PLANOGRAMS_BY_STORE, storeId, filters],
        queryFn: ({ pageParam = 1 }) =>
            getPlanogramsByStore(storeId as number, {
                page: pageParam,
                perPage: PER_PAGE,
                ...filters,
            }),
        getNextPageParam: (lastPage) => {
            const { current_page, last_page } = lastPage.meta;
            return current_page < last_page ? current_page + 1 : undefined;
        },
        enabled: !!storeId,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) =>
            !NON_RETRYABLE_STATUSES.includes(getErrorStatus(error) as number) &&
            failureCount < 3,
    });

    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

    const planograms = useMemo<QuickAdjustPlanogram[]>(
        () => query.data?.pages.flatMap((page) => page.data) ?? [],
        [query.data],
    );

    const total = query.data?.pages[0]?.meta.total ?? 0;

    return {
        planograms,
        total,
        isLoading: query.isLoading,
        isError: query.isError,
        isSuccess: query.isSuccess,
        errorStatus: getErrorStatus(query.error),
        refetch: query.refetch,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    };
}
